import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { useSyncExternalStore } from "react";
import translations from "./translations.json";

export type StoreLanguage = { code: string; nativeName: string; direction: "rtl" | "ltr"; flagCountryCode?: string; isDefault: boolean };
export const languageStorageKey = `portal:language:${window.location.host}`;
const listeners = new Set<() => void>();
let revision = 0;
let language = "fa";
let directions: Record<string, "rtl" | "ltr"> = { fa: "rtl", en: "ltr", ar: "rtl" };
let desiredLanguage = "fa";
try { language = localStorage.getItem(languageStorageKey) || "fa"; } catch { /* Private browsing may disable persistence. */ }
if (window.location.pathname === "/invitation" || window.location.pathname === "/login" || window.location.pathname === "/register") {
  const requestedLanguage = getRequestedStoreLanguage(window.location.search);
  if (requestedLanguage) language = requestedLanguage;
}
desiredLanguage = language;
if (!["fa", "en", "ar"].includes(language)) language = "fa";
const resources = Object.fromEntries(["fa", "en", "ar"].map(function (code) {
  return [code, { translation: Object.fromEntries(Object.entries(translations).map(function ([key, values]) {
    return [key, code === "fa" ? key : values[code === "en" ? 0 : 1]];
  })) }];
}));
Object.assign(resources.fa.translation, { rugCount_one: "{{formatted}} تخته", rugCount_other: "{{formatted}} تخته" });
Object.assign(resources.en.translation, { rugCount_one: "{{formatted}} rug", rugCount_other: "{{formatted}} rugs" });
Object.assign(resources.ar.translation, {
  rugCount_zero: "لا سجاد", rugCount_one: "سجادة واحدة", rugCount_two: "سجادتان",
  rugCount_few: "{{formatted}} سجادات", rugCount_many: "{{formatted}} سجادة", rugCount_other: "{{formatted}} سجادة",
});
Object.assign(resources.fa.translation, { sizeCount_one: "{{formatted}} سایز", sizeCount_other: "{{formatted}} سایز" });
Object.assign(resources.en.translation, { sizeCount_one: "{{formatted}} size", sizeCount_other: "{{formatted}} sizes" });
Object.assign(resources.ar.translation, {
  sizeCount_zero: "{{formatted}} مقاسات", sizeCount_one: "{{formatted}} مقاس", sizeCount_two: "{{formatted}} مقاسين",
  sizeCount_few: "{{formatted}} مقاسات", sizeCount_many: "{{formatted}} مقاسًا", sizeCount_other: "{{formatted}} مقاس",
});
void i18next.use(initReactI18next).init({
  resources, lng: language, fallbackLng: "fa", keySeparator: false, nsSeparator: false,
  initImmediate: false, returnEmptyString: false, interpolation: { escapeValue: false },
});

export function isStorePath(path = window.location.pathname): boolean {
  return /^\/(categories|products|search|schema|schemaId|account|cart|showroom|agency|customer|shop|category|product|my|profile|basket)(\/|$)/.test(path)
    || path === "/register" || path === "/invitation" || path === "/" || path === "/login";
}

export function getLanguage(): string {
  return isStorePath() ? language : "fa";
}

export function getRequestedStoreLanguage(search: string): string | null {
  const requestedLanguage = new URLSearchParams(search).get("language");
  return requestedLanguage && ["fa", "en", "ar"].includes(requestedLanguage)
    ? requestedLanguage
    : null;
}

export function getLanguageRevision(): number { return revision; }
export function getDirection(): "rtl" | "ltr" { return directions[getLanguage()] || "rtl"; }
export function getLocale(): string {
  return getLanguage() === "fa" ? "fa-IR" : getLanguage() === "ar" ? "ar-SA-u-ca-gregory" : getLanguage() === "en" ? "en-GB" : getLanguage();
}

export function t(key: string, values?: Record<string, unknown>): string {
  return localizeDigits(String(i18next.t(key, { ...values, lng: getLanguage(), defaultValue: key })));
}

/** Display-only digit conversion: preserves leading zeroes, separators and code precision. */
export function localizeDigits(value: string | number | null | undefined): string {
  const digits = getLanguage() === "fa" ? "۰۱۲۳۴۵۶۷۸۹" : getLanguage() === "ar" ? "٠١٢٣٤٥٦٧٨٩" : "0123456789";
  return String(value ?? "").replace(/[0-9۰-۹٠-٩]/g, function (digit) {
    const code = digit.charCodeAt(0);
    return digits[code >= 1776 ? code - 1776 : code >= 1632 ? code - 1632 : code - 48];
  });
}

export function parseLocalizedNumber(value: string | undefined): number {
  const normalized = String(value ?? "").replace(/[۰-۹٠-٩]/g, function (digit) {
    const code = digit.charCodeAt(0);
    return String(code >= 1776 ? code - 1776 : code - 1632);
  }).replace(/[٬,\s\u200e\u200f\u061c]/g, "").replace(/٫/g, ".").replace(/−/g, "-");
  return normalized.trim() ? Number(normalized) : NaN;
}

export function formatRugCount(count: number): string {
  return String(i18next.t("rugCount", { lng: getLanguage(), count, formatted: count.toLocaleString(getLocale(), { maximumFractionDigits: 2 }) }));
}

export function formatSizeCount(count: number): string {
  return String(i18next.t("sizeCount", { lng: getLanguage(), count, formatted: count.toLocaleString(getLocale()) }));
}

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return function () { listeners.delete(listener); };
}

export function useStoreLanguage(): string {
  return useSyncExternalStore(subscribeLanguage, getLanguage);
}

export function applyDocumentLanguage(): void {
  document.documentElement.lang = getLanguage();
  document.documentElement.dir = getDirection();
  document.documentElement.style.direction = getDirection();
  document.documentElement.dataset.storeLanguage = isStorePath() ? getLanguage() : "";
  if (isStorePath()) document.title = `${t("کاتالوگ")} · Raj`;
  else document.title = "سامانه مدیریت عملیات فرش";
}

export function activateLanguage(code: string): void {
  desiredLanguage = code;
  if (!Object.prototype.hasOwnProperty.call(directions, code)) return;
  try { localStorage.setItem(languageStorageKey, code); } catch { /* Keep working without storage. */ }
  if (code !== language) {
    language = code;
    revision++;
    void i18next.changeLanguage(code);
    applyDocumentLanguage();
    listeners.forEach(function (listener) { listener(); });
  }
}

export function configureLanguages(available: StoreLanguage[], requestedLanguage?: string): void {
  directions = { fa: "rtl", en: "ltr", ar: "rtl", ...Object.fromEntries(available.map(function (item) {
    return [item.code, item.direction];
  })) };
  const selected = available.find(function (item) { return item.code === desiredLanguage; });
  const fallback = available.find(function (item) { return item.isDefault; }) || available[0];
  const explicitLanguage = requestedLanguage && ["fa", "en", "ar"].includes(requestedLanguage)
    ? requestedLanguage : null;
  activateLanguage(explicitLanguage || selected?.code || fallback?.code || "fa");
}

applyDocumentLanguage();
// Agency language is session-owned; another tab must not override it through storage.
